import { handleRefreshToken, handleSignOut } from '@common/utils/handleToken';
import { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { handleErrorLog } from '@common/utils/handleErrorLogs';
import { getAppName } from '@libs/utils/helpers/getAppName';
import { useUserStore } from '@libs/acp/store/user/useUserStore';

//#region Request Interceptor
async function applyRefreshTokenHeader(config: InternalAxiosRequestConfig, refreshToken: string | undefined) {
  if (refreshToken) {
    config.headers['Refresh-Token'] = refreshToken;
  } else {
    handleErrorLog(new Error('Attempting refresh token request without a refresh token.'));
    await handleSignOut();
    throw new Error('Missing refresh token for refresh operation');
  }
}

function applyAccessTokenHeaders(config: InternalAxiosRequestConfig, accessToken: string) {
  config.headers.token = accessToken;
  config.headers['X-Auth-Token'] = accessToken;
  config.headers.Authorization = `Bearer ${accessToken}`;
}

async function manageAuthHeadersForRequest(config: InternalAxiosRequestConfig) {
  const url = config.url || '';
  const isPublicRequest = url.includes('/public');
  const isEngageSignin = url.includes('/engage/signin');
  const isLogoutRequest = url.includes('/logout');
  const isRefreshTokenRequestRoute = url.includes('/refresh-token');

  if (isEngageSignin || (isPublicRequest && !isRefreshTokenRequestRoute)) {
    return;
  }

  const tokenData = useUserStore.getState().authenticatedUser.acp.auth;

  if (!isRefreshTokenRequestRoute && (!tokenData.access_token || !tokenData.refresh_token)) {
    await handleSignOut();
    throw new Error('Missing access or refresh token, aborting request');
  }

  if (isRefreshTokenRequestRoute) {
    await applyRefreshTokenHeader(config, tokenData.refresh_token);
  } else {
    if (tokenData.access_token) {
      applyAccessTokenHeaders(config, tokenData.access_token);
    }
    if (isLogoutRequest && tokenData.refresh_token) {
      config.headers['Refresh-Token'] = tokenData.refresh_token;
    }
  }
}

export function setupRequestInterceptor(
  apiInstance: AxiosInstance,
  onBeforeRequest?: (config: InternalAxiosRequestConfig) => Promise<InternalAxiosRequestConfig>
) {
  apiInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      try {
        config.headers = config.headers || {};
        config.headers['Content-Type'] = 'application/json';
        config.headers['X-App-Id'] = getAppName();

        await manageAuthHeadersForRequest(config);

        if (onBeforeRequest) {
          return await onBeforeRequest(config);
        }

        return config;
      } catch (error) {
        handleErrorLog(error);
        return Promise.reject(error);
      }
    },
    (error) => {
      handleErrorLog(error);
      return Promise.reject(error);
    }
  );
}
//#endregion Request Interceptor

//#region Response Interceptor
export interface AuthTokenResponse {
  access_token: string;
  refresh_token?: string;
}

let isRefreshingToken = false;
let failedRequestsQueue: Array<{
  onSuccess: (token: string) => void;
  onFailure: (error: AxiosError) => void;
  originalConfig: InternalAxiosRequestConfig;
}> = [];

function enqueueRequestForRetry(
  originalRequest: InternalAxiosRequestConfig,
  resolve: (value: unknown) => void,
  reject: (reason?: any) => void,
  apiInstance: AxiosInstance
) {
  failedRequestsQueue.push({
    onSuccess: (newAccessToken: string) => {
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      }
      resolve(apiInstance(originalRequest));
    },
    onFailure: (err: AxiosError) => {
      reject(err);
    },
    originalConfig: originalRequest,
  });
}

function processQueuedRequests(newAccessToken: string) {
  failedRequestsQueue.forEach((request) => {
    request.onSuccess(newAccessToken);
  });
}

function rejectQueuedRequests(error: AxiosError) {
  failedRequestsQueue.forEach((request) => {
    request.onFailure(error);
  });
}

async function attemptTokenRefreshAndRetry(
  apiInstance: AxiosInstance,
  originalRequest: InternalAxiosRequestConfig & { _retry?: boolean },
  authError: AxiosError<any>
) {
  return new Promise(async (resolve, reject) => {
    try {
      const newTokens = (await handleRefreshToken()) as AuthTokenResponse | null;

      if (!newTokens || !newTokens.access_token) {
        rejectQueuedRequests(authError);
        reject(authError);
        return;
      }

      const newAccessToken = newTokens.access_token;

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      }

      processQueuedRequests(newAccessToken);
      resolve(apiInstance(originalRequest));
    } catch (refreshError: any) {
      rejectQueuedRequests(refreshError);
      reject(refreshError);
    } finally {
      isRefreshingToken = false;
      failedRequestsQueue = [];
    }
  });
}

export function setupResponseInterceptor(apiInstance: AxiosInstance) {
  apiInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<any>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (error.response?.data?.error_slug === 'customer_not_found') {
        await handleSignOut();
        return Promise.reject(error);
      }

      const isAuthError = error.response?.status === 401;

      if (isAuthError && originalRequest && !originalRequest._retry) {
        if (isRefreshingToken) {
          return new Promise((resolve, reject) => {
            enqueueRequestForRetry(originalRequest, resolve, reject, apiInstance);
          });
        }

        originalRequest._retry = true;
        isRefreshingToken = true;

        return attemptTokenRefreshAndRetry(apiInstance, originalRequest, error);
      }

      return Promise.reject(error);
    }
  );
}
//#endregion Response Interceptor

/**
 * Sets up the API interceptors for the provided Axios instance.
 * This function configures both request and response interceptors.
 *
 * @param {AxiosInstance} apiInstance - The Axios instance to set up interceptors for.
 * @returns {AxiosInstance} - The configured Axios instance with interceptors.
 */
export function setupApiInterceptors(
  apiInstance: AxiosInstance,
  onBeforeRequest?: (config: InternalAxiosRequestConfig) => Promise<InternalAxiosRequestConfig>
): AxiosInstance {
  setupRequestInterceptor(apiInstance, onBeforeRequest);
  setupResponseInterceptor(apiInstance);
  return apiInstance;
}
